// TenderRegister — the Town Council's list of its own tenders. The first screen that renders real
// data from the database rather than a placeholder.
//
// Connects to: GET /api/townforge/tenders/{orgId} (thin-router/routers/townforge_tenders.py), which
// reads tf_tc_<org_id>.tenders. The org id is taken from /me and NEVER from the URL or a form — a
// caller who could name their own org id would cross a tenant boundary with one edited request.
//
// Three states, and the distinction between the last two is the point (TD-034):
//   loading  — say so
//   error    — say what failed. An error must NEVER fall through to an empty list, because in this
//              product "no tenders" is a statement an officer will act on. A failed fetch rendering
//              as an empty register is a lie with consequences.
//   empty    — say that there are genuinely none
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api, ApiError, type Tender } from '../lib/api'

// The four-tone status palette from design-principles.md §1.3 — the same everywhere, never a second
// system. Most lifecycle states are neutral because §1.5 allows only one urgent thing per view:
// if every row is toned, nothing reads as needing attention.
const TONE: Record<string, 'good' | 'warn' | 'danger' | 'neutral'> = {
  draft: 'neutral',
  internal_review: 'neutral',
  published: 'good',
  open: 'good',
  closed: 'warn',
  opened: 'warn',
  clarification: 'warn',
  evaluation: 'warn',
  recommendation: 'warn',
  approval: 'warn',
  awarded: 'good',
  not_awarded: 'neutral',
  cancelled: 'danger',
}

const LABEL: Record<string, string> = {
  draft: 'Draft',
  internal_review: 'Internal review',
  published: 'Published',
  open: 'Open',
  closed: 'Closed',
  opened: 'Opened',
  clarification: 'Clarification',
  evaluation: 'Evaluation',
  recommendation: 'Recommendation',
  approval: 'Approval',
  awarded: 'Awarded',
  not_awarded: 'Not awarded',
  cancelled: 'Cancelled',
}

/** Money as it appears on a procurement record: grouped, two decimals, never abbreviated. A tender
 *  sum shown as "4.7m" is not a figure anybody can check against a bill of quantities. */
function money(v: number | null): string {
  if (v === null || v === undefined) return '—'
  return v.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** An absolute time WITH its timezone (design-principles.md §2.2). A closing time is the most
 *  consequential value on this screen; rendering it without a zone invites exactly the misreading
 *  that costs a tenderer their bid. Remaining time is deliberately NOT computed here — it must come
 *  from a server reference (TD-010), which the register does not fetch. */
function when(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-SG', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  })
}

export default function TenderRegister() {
  const { me } = useAuth()
  const orgId = me?.org?.id ?? null

  const [tenders, setTenders] = useState<Tender[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    setTenders(null)
    setError(null)
    api.tenders.list(orgId)
      .then(r => { if (!cancelled) setTenders(r.tenders) })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof ApiError
          ? `Could not load the tender register: ${e.detail}`
          : 'Could not reach TownForge.')
      })
    return () => { cancelled = true }
  }, [orgId])

  // A superuser has no membership, so /me resolves no org. Say that plainly rather than showing an
  // empty register, which would read as "this Town Council has called no tenders".
  if (!orgId) {
    return (
      <div className="page">
        <h1>Tender register</h1>
        <p className="page-state">
          Your account is not a member of a Town Council, so there is no register to show.
        </p>
      </div>
    )
  }

  if (error) return (
    <div className="page">
      <h1>Tender register</h1>
      <p className="page-state page-state-error">{error}</p>
    </div>
  )

  if (tenders === null) return (
    <div className="page">
      <h1>Tender register</h1>
      <p className="page-state">Loading…</p>
    </div>
  )

  return (
    <div className="page">
      <header className="page-head">
        <h1>Tender register</h1>
        <p className="page-sub">
          {tenders.length} {tenders.length === 1 ? 'tender' : 'tenders'} · {me?.org?.name}
        </p>
        {/* Capability is enforced server-side; this link is usability (TD-030). A role without
            tender.author gets a 403 from the API, so the button being present is not the control. */}
        <Link to="new" className="btn-inline">Call a tender</Link>
      </header>

      {tenders.length === 0 ? (
        <p className="page-state">No tenders have been called yet.</p>
      ) : (
        // A LIST, not a bordered grid (design-principles.md §0 and §1.1): alignment carries the
        // structure, and a border is reserved for things you can act on.
        <ul className="reg">
          {tenders.map(t => (
            <li key={t.id} className="reg-row">
              <Link to={t.id} className="reg-main">
                <span className="reg-ref mono">{t.tenderRef}</span>
                <span className="reg-title">{t.title}</span>
              </Link>
              <div className="reg-meta">
                <span className={`tone tone-${TONE[t.status] ?? 'neutral'}`}>
                  {LABEL[t.status] ?? t.status}
                </span>
                <span className="reg-val">
                  <span className="reg-lbl">Estimated</span>
                  <span className="reg-num">${money(t.estimatedValue)}</span>
                </span>
                <span className="reg-val">
                  <span className="reg-lbl">Closes</span>
                  <span className="reg-when">{when(t.closesAt)}</span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
